import { Branding } from './components/Branding.tsx';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { ThemeProvider } from './contexts/ThemeContext.tsx';
import { ProtectedRoute } from './components/ProtectedRoute.tsx';
import { Login } from './pages/Login.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Projects } from './pages/Projects.tsx';
import { Users } from './pages/Employees.tsx';
import { EmployeeProfile } from './pages/EmployeeProfile.tsx';
import { EmployeeEdit } from './pages/EmployeeEdit.tsx';
import { EmployeePermissions } from './pages/EmployeePermissions.tsx';
import { Configuration } from './pages/Configuration.tsx';
import { Settings } from './pages/Settings.tsx';
import { OdooConfigurations } from './pages/OdooConfigurations.tsx';
import { ForgotPassword } from './pages/ForgotPassword.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { SyncProvider } from './contexts/SyncContext.tsx';
import { Notifications } from './pages/Notifications.tsx';
import { MantineProvider } from '@mantine/core';
import { Notifications as NotificationsHost } from '@mantine/notifications';
import { theme } from './theme.ts';
import { Sales } from './pages/Sales.tsx';
import { Toll } from './pages/Fleet/Toll/Toll.tsx';
import { PersonalUse } from './pages/Fleet/Personal_use/PersonalUse.tsx';
import { CreditLimitApply } from './pages/CreditLimit/CreditLimitApply.tsx';
import { CreditLimitExtension } from './pages/CreditLimit/CreditLimitExtension.tsx';
import { TemporaryCredit } from './pages/CreditLimit/TemporaryCredit.tsx';
import { Fuel } from './pages/Fleet/Fuel/Fuel.tsx';
import { Maintainance } from './pages/Fleet/Maintainance/Maintainance.tsx';
import { DailyActivity } from './pages/DailyActivity/DailyActivity.tsx';
import Advertisement from './pages/Advertisement.tsx';
import { Expense } from './pages/Expense.tsx';
import CrossMatchFuel from './pages/Fleet/Fuel/Cross-Match/CrossMatchFuel.tsx';
import CrossMatchMaintenance from './pages/Fleet/Maintainance/Cross-Match/CrossMatchMaintenance.tsx';
import CrossMatchToll from './pages/Fleet/Toll/Cross-Match/CrossMatchToll.tsx';
import CrossMatchPersonalUse from './pages/Fleet/Personal_use/Cross-Match/CrossMatchPersonalUse.tsx';
import CrossMatchExpense from './pages/CrossMatchExpense.tsx';
import CrossMatchDailyActivity from './pages/CrossMatchDailyActivity.tsx';
import { Reports } from './pages/Reports.tsx';
import { OdooQueue } from './pages/OdooQueue.tsx';
import { AppUpdates } from './pages/AppUpdates.tsx';
import { ClientActivity } from './pages/ClientActivity.tsx';
import { FilterMemory } from './components/FilterMemory.tsx';

function App() {
  return (
    // "auto" follows the operating system until the user picks a scheme;
    // the choice is remembered in localStorage by Mantine.
    <MantineProvider theme={theme} defaultColorScheme="auto">
    <NotificationsHost position="top-right" />
    <ThemeProvider>
      <SyncProvider>
        <AuthProvider>
          <Branding />
          <BrowserRouter>
            <FilterMemory>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sales"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Sales />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fleet/toll"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Toll />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fleet/personal-use"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <PersonalUse />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fleet/personal-use/cross-match"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <CrossMatchPersonalUse />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/*"
                element={
                  <ProtectedRoute allowedRoles={['super_admin']}>
                    <Projects />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fleet/toll"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Toll />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fleet/fuel"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Fuel />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fleet/fuel/cross-match"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <CrossMatchFuel />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fleet/maintenance"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Maintainance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fleet/maintenance/cross-match"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <CrossMatchMaintenance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fleet/toll/cross-match"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <CrossMatchToll />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/daily-activity"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <DailyActivity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Users />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/new"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <EmployeeEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/:id"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <EmployeeProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/:id/edit"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <EmployeeEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/:id/permissions"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <EmployeePermissions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuration"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Configuration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/odoo-configurations"
                element={
                  <ProtectedRoute allowedRoles={['super_admin']}>
                    <OdooConfigurations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/advertisement"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Advertisement />
                  </ProtectedRoute>
                }
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/credit-limit-apply"
                element={<CreditLimitApply />}
              />
              <Route path="/temporary-credit" element={<TemporaryCredit />} />
              <Route
                path="/credit-limit-extension"
                element={<CreditLimitExtension />}
              />
              <Route path="/expense" element={<Expense />} />
              <Route
                path="/expense/cross-match"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <CrossMatchExpense />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/daily-activity/cross-match"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <CrossMatchDailyActivity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/odoo-queue"
                element={
                  <ProtectedRoute>
                    <OdooQueue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app-updates"
                element={
                  <ProtectedRoute allowedRoles={['super_admin']}>
                    <AppUpdates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-activity"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <ClientActivity />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </FilterMemory>
          </BrowserRouter>
        </AuthProvider>

      </SyncProvider>
    </ThemeProvider>
    </MantineProvider>
  );
}

export default App;
